import { useEffect, useRef, useState } from "react";
import { FlipHorizontal2, FlipVertical2, Maximize, Minimize, X } from "lucide-react";

/** Keep display controls independent of the continuously rendering script. */
export function TalentControls({ connected, title, flipX, flipY, onMirror, onFlip }: {
  connected: boolean;
  title: string;
  flipX: boolean;
  flipY: boolean;
  onMirror: () => void;
  onFlip: () => void;
}) {
  const [visible, setVisible] = useState(true);
  const [fullscreen, setFullscreen] = useState(Boolean(document.fullscreenElement));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const bar = useRef<HTMLDivElement>(null);
  const fullscreenButton = useRef<HTMLButtonElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusFrame = useRef<number | null>(null);
  const showing = useRef(true);
  const hovering = useRef(false);
  const helpVisible = useRef(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const failed = useRef(false);
  const pointer = useRef<{ x: number; y: number } | null>(null);
  const hiddenAt = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  const hide = () => {
    clearTimer();
    hiddenAt.current = pointer.current;
    showing.current = false;
    hovering.current = false;
    helpVisible.current = false;
    failed.current = false;
    setError("");
    setVisible(false);
  };
  const arm = () => {
    clearTimer();
    if (!showing.current || hovering.current || helpVisible.current || failed.current || busy.current || bar.current?.contains(document.activeElement)) return;
    timer.current = setTimeout(() => {
      if (!hovering.current && !helpVisible.current && !bar.current?.contains(document.activeElement)) hide();
    }, 5000);
  };
  const show = () => {
    showing.current = true;
    setVisible(true);
    arm();
  };
  useEffect(() => {
    mounted.current = true;
    const move = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY };
      if (!showing.current && hiddenAt.current && Math.hypot(event.clientX - hiddenAt.current.x, event.clientY - hiddenAt.current.y) < 12) return;
      // Layout-induced events at the same point must not undo a dismissal.
      show();
    };
    const press = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY };
      if (!showing.current) show();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || showing.current) return;
      event.preventDefault();
      show();
      focusFrame.current = requestAnimationFrame(() => fullscreenButton.current?.focus());
    };
    const changed = () => {
      const active = Boolean(document.fullscreenElement);
      setFullscreen(active);
      if (!active) show();
    };
    const helpChanged = (event: Event) => {
      if (!bar.current?.contains(event.target as Node)) return;
      helpVisible.current = Boolean((event as CustomEvent<{ visible: boolean }>).detail.visible);
      if (helpVisible.current) clearTimer();
      else arm();
    };
    document.addEventListener("cue-help-visible", helpChanged);
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerdown", press);
    document.addEventListener("keydown", key);
    document.addEventListener("fullscreenchange", changed);
    arm();
    return () => {
      mounted.current = false;
      clearTimer();
      if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current);
      document.removeEventListener("cue-help-visible", helpChanged);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerdown", press);
      document.removeEventListener("keydown", key);
      document.removeEventListener("fullscreenchange", changed);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    failed.current = false;
    setError("");
    clearTimer();
    const exiting = Boolean(document.fullscreenElement);
    try {
      if (exiting) await document.exitFullscreen();
      else if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
      else throw new Error("Fullscreen unavailable");
      if (!mounted.current) return;
      setFullscreen(Boolean(document.fullscreenElement));
      if (exiting) show();
      else hide();
    } catch {
      if (!mounted.current) return;
      failed.current = true;
      setError(exiting ? "Could not exit fullscreen. Press Esc or use your browser's fullscreen command." : "Could not enter fullscreen. Try again or use your browser's fullscreen command.");
      show();
    } finally {
      busy.current = false;
      if (mounted.current) { setPending(false); arm(); }
    }
  };

  if (!visible) return null;
  return <div
    ref={bar}
    className="talent-chrome"
    role="toolbar"
    aria-label="Talent display controls"
    onPointerEnter={() => { hovering.current = true; clearTimer(); }}
    onPointerLeave={() => { hovering.current = false; arm(); }}
    onFocusCapture={clearTimer}
    onBlurCapture={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        queueMicrotask(() => { if (mounted.current) arm(); });
      }
    }}
  >
    <span>
      <span className={`status-dot ${connected ? "" : "disconnected"}`} />
      <span className="talent-session-name" title={title}>{connected ? "Connected to operator" : "Waiting for operator"} · {title}</span>
    </span>
    <div className="talent-display-actions">
      <button
        type="button"
        className="talent-orientation"
        aria-label="Mirror horizontally"
        aria-pressed={flipX}
        data-help="Mirror this talent screen left to right. Other screens stay unchanged. Reload this tab to use the operator's orientation again."
        onClick={onMirror}
      ><FlipHorizontal2 size={18} /><span>Mirror</span></button>
      <button
        type="button"
        className="talent-orientation"
        aria-label="Flip vertically"
        aria-pressed={flipY}
        data-help="Flip this talent screen top to bottom. Other screens stay unchanged. Reload this tab to use the operator's orientation again."
        onClick={onFlip}
      ><FlipVertical2 size={18} /><span>Flip</span></button>
      <button
        ref={fullscreenButton}
        type="button"
        disabled={pending}
        aria-pressed={fullscreen}
        data-help={fullscreen ? "Leave fullscreen and return this talent display to its window." : "Fill this screen with the talent display. The controls hide while you read."}
        onClick={() => void toggleFullscreen()}
      >
        {fullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        {fullscreen ? "Exit fullscreen" : "Fullscreen"}
      </button>
      <button
        type="button"
        aria-label="Hide display controls"
        data-help="Hide display controls. Move the pointer or tap to show them again."
        onClick={hide}
      ><X size={18} /></button>
    </div>
    {error && <p className="talent-control-error" role="alert">{error}</p>}
  </div>;
}
